import { useAtomValue } from 'jotai';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { IconSvg } from '@/lib/v2/components';
import { TabItemProps } from '@/lib/v2/components/Tab/TabItem';
import { TabPaneProps } from '@/lib/v2/components/Tab/TabPane';
import { ModalActionCreation, ModalSingleInput } from '@/lib/v2/examples';

import { useUserData } from '@/src/application/hooks/useUserData';
import configData from '@/src/config.json';
import { useService } from '@/src/infrastructure/Protocol/useEmblue';

import AllsTabPaneContainer from './AllsTabPaneContainer';

import { atomDetailCampaign } from '@/modules/CampaignsModule/atoms/campaignAtom';
import { useCreateAction } from '@/modules/CampaignsModule/hooks/useCreateAction';
import { ActionTypeName } from '@/modules/CampaignsModule/interfaces/Campaigns';

type CreateActionsModalProps = {
  isOpen: boolean;
  onClose: () => void;
};

const CreateActionsModal = ({ isOpen, onClose }: CreateActionsModalProps) => {
  const { t } = useTranslation();
  const [userData] = useUserData();
  const { id: campaignId } = useParams();
  const {
    handleSubmit,
    handleOpenCreateActionNameModal,
    createActionIsLoading,
    createActionNameModal,
    handleCloseCreateItem,
    restrictions,
  } = useCreateAction(campaignId ?? '');

  const { name: campaignName } = useAtomValue(atomDetailCampaign);
  const service = useService();

  const clickLabel = useCallback(
    (action: ActionTypeName) => {
      if (!userData.email.includes('@embluemail.com')) void service.sendUpgradeInterest(action);

      // cspell:disable
      window.open(configData.BUY_EMBLUE_PLANS + '?contrato-id=' + userData.contractId, '_blank');
      // cspell:enable
    },
    [service, userData.contractId]
  );

  const tabItems = useMemo<TabItemProps[]>(() => {
    return [
      {
        children: t('CREATE_ACTIONS_MODAL.alls'),
        tabId: 1,
        id: 'all-tab',
      },
      {
        children: t('CREATE_ACTIONS_MODAL.emails'),
        tabId: 2,
        id: 'emails-tab',
      },
      {
        children: t('CREATE_ACTIONS_MODAL.sms'),
        tabId: 3,
        id: 'sms-tab',
      },
    ];
  }, [t]);

  const tabPanes = useMemo<TabPaneProps[]>(() => {
    return [
      {
        tabId: 1,
        children: (
          <AllsTabPaneContainer
            restrictions={restrictions}
            onClose={onClose}
            onLabelClick={clickLabel}
            onOpenCreateActionNameModal={handleOpenCreateActionNameModal}
          />
        ),
      },
      {
        tabId: 2,
        children: (
          <AllsTabPaneContainer
            filterBy="email"
            restrictions={restrictions}
            onClose={onClose}
            onLabelClick={clickLabel}
            onOpenCreateActionNameModal={handleOpenCreateActionNameModal}
          />
        ),
      },
      {
        tabId: 3,
        children: (
          <AllsTabPaneContainer
            filterBy="sms"
            restrictions={restrictions}
            onClose={onClose}
            onLabelClick={clickLabel}
            onOpenCreateActionNameModal={handleOpenCreateActionNameModal}
          />
        ),
      },
    ];
  }, [clickLabel, handleOpenCreateActionNameModal, onClose, restrictions]);

  const handleOnSubmitCreateItem = useCallback(
    (actionName: string) => {
      void handleSubmit(actionName, campaignName ?? '');
    },
    [campaignName, handleSubmit]
  );

  return (
    <>
      <ModalSingleInput
        isLoading={createActionIsLoading}
        isOpen={createActionNameModal}
        title={t('CREATE_ACTIONS_MODAL.title')}
        onClose={handleCloseCreateItem}
        onSubmit={handleOnSubmitCreateItem}
      />
      <ModalActionCreation
        currentTab={1}
        icon={
          <IconSvg
            rounded
            bgColor="primary"
            height="40px"
            svgComponent={
              <svg
                fill="none"
                height="25"
                viewBox="0 0 25 25"
                width="25"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  clipRule="evenodd"
                  d="M5.93751 11.7783H3.10835C2.8997 11.7784 2.69333 11.8212 2.50219 11.9039C2.31105 11.9867 2.13925 12.1076 1.99759 12.259C1.71604 12.5607 1.56057 12.9567 1.56252 13.3671V18.1481C1.56174 18.5573 1.71711 18.9517 1.99759 19.2526C2.13925 19.4041 2.31105 19.525 2.50219 19.6077C2.69333 19.6904 2.8997 19.7332 3.10835 19.7333H4.25436L8.53335 21.8017C8.66233 21.8641 8.80707 21.8874 8.9494 21.8687C9.09174 21.8501 9.22534 21.7903 9.33345 21.6969C9.44156 21.6034 9.51937 21.4805 9.55712 21.3435C9.59488 21.2065 9.59089 21.0615 9.54567 20.9268L9.13855 19.7105L11.6833 19.7261C11.892 19.726 12.0984 19.6832 12.2895 19.6005C12.4806 19.5178 12.6524 19.3969 12.7941 19.2454C13.0729 18.9464 13.2281 18.5548 13.2292 18.1481V14.3346H15.2405L14.4007 16.5761C14.3474 16.7182 14.3408 16.8732 14.3817 17.0193C14.4226 17.1653 14.509 17.2949 14.6286 17.3896C14.7482 17.4843 14.8949 17.5394 15.0479 17.5469C15.201 17.5545 15.3525 17.5142 15.4811 17.4318L20.3033 14.3358H21.4238C21.6907 14.3353 21.9548 14.2819 22.2005 14.1786C22.4462 14.0754 22.6685 13.9245 22.8542 13.7349C23.2286 13.3533 23.4378 12.8424 23.4375 12.3107V5.14891C23.4379 4.61779 23.2291 4.1074 22.8554 3.72592C22.6697 3.53629 22.4474 3.3854 22.2017 3.28219C21.9561 3.17897 21.6919 3.12552 21.425 3.125H7.95001C7.68308 3.12552 7.41896 3.17897 7.17328 3.28219C6.92759 3.3854 6.70533 3.53629 6.51963 3.72592C6.14595 4.1074 5.93715 4.61779 5.93751 5.14891V11.7783ZM11.7708 13.3671V18.1481C11.7716 18.1921 11.756 18.2348 11.7271 18.2683C11.7171 18.2808 11.7029 18.2894 11.687 18.2923L8.12501 18.2683C8.00905 18.2675 7.89458 18.2942 7.7911 18.3459C7.68763 18.3977 7.59813 18.4732 7.53004 18.566C7.46196 18.6588 7.41724 18.7664 7.39961 18.8797C7.38197 18.9931 7.39193 19.109 7.42866 19.2177L7.60852 19.7502L4.7429 18.3632C4.64337 18.3151 4.53407 18.29 4.42328 18.2899H3.10835C3.09979 18.2891 3.09149 18.2865 3.08397 18.2824C3.07644 18.2782 3.06985 18.2726 3.0646 18.2659C3.03567 18.2324 3.02011 18.1897 3.02085 18.1457V13.3671C3.02011 13.3231 3.03567 13.2804 3.0646 13.247C3.06985 13.2402 3.07644 13.2346 3.08397 13.2305C3.09149 13.2263 3.09979 13.2238 3.10835 13.2229H11.6833C11.6919 13.2238 11.7002 13.2263 11.7077 13.2305C11.7153 13.2346 11.7218 13.2402 11.7271 13.247C11.756 13.2804 11.7716 13.3231 11.7708 13.3671ZM13.1587 12.8864H16.288C16.4056 12.8864 16.5214 12.9144 16.6256 12.9682C16.7298 13.022 16.8193 13.0999 16.8865 13.1953C16.9537 13.2906 16.9966 13.4007 17.0115 13.516C17.0265 13.6313 17.013 13.7485 16.9722 13.8575L16.5335 15.0305L19.6932 13.0018C19.8111 12.9245 19.9492 12.8827 20.0906 12.8816H21.4274C21.4994 12.8812 21.5706 12.8666 21.6368 12.8385C21.703 12.8104 21.7628 12.7695 21.8127 12.7182C21.9214 12.606 21.9819 12.4565 21.9816 12.3011V5.14891C21.9819 4.99353 21.9214 4.84406 21.8127 4.73187C21.7628 4.68053 21.703 4.63962 21.6368 4.61154C21.5706 4.58346 21.4994 4.5688 21.4274 4.56842H7.95001C7.87801 4.5688 7.80682 4.58346 7.74064 4.61154C7.67447 4.63962 7.61467 4.68053 7.56477 4.73187C7.45606 4.84406 7.39552 4.99353 7.39585 5.14891V11.7783H11.6833C11.892 11.7784 12.0984 11.8212 12.2895 11.9039C12.4806 11.9867 12.6524 12.1076 12.7941 12.259C12.9632 12.4397 13.0879 12.6564 13.1587 12.8924V12.8864ZM19.0625 6.73054C18.7741 6.73054 18.4921 6.81513 18.2523 6.9736C18.0125 7.13207 17.8256 7.35732 17.7152 7.62085C17.6048 7.88438 17.5759 8.17436 17.6322 8.45412C17.6885 8.73389 17.8274 8.99086 18.0313 9.19256C18.2353 9.39426 18.4951 9.53162 18.778 9.58727C19.0609 9.64292 19.3541 9.61435 19.6206 9.5052C19.8871 9.39604 20.1148 9.21119 20.2751 8.97401C20.4353 8.73684 20.5208 8.458 20.5208 8.17276C20.5208 7.98337 20.4831 7.79583 20.4098 7.62085C20.3365 7.44587 20.2291 7.28688 20.0937 7.15296C19.9583 7.01904 19.7975 6.9128 19.6206 6.84033C19.4437 6.76785 19.254 6.73054 19.0625 6.73054ZM14.6875 6.73054C14.3991 6.73054 14.1171 6.81513 13.8773 6.9736C13.6375 7.13207 13.4506 7.35732 13.3402 7.62085C13.2298 7.88438 13.2009 8.17436 13.2572 8.45412C13.3135 8.73389 13.4524 8.99086 13.6563 9.19256C13.8603 9.39426 14.1201 9.53162 14.403 9.58727C14.6859 9.64292 14.9791 9.61435 15.2456 9.5052C15.5121 9.39604 15.7398 9.21119 15.9001 8.97401C16.0603 8.73684 16.1458 8.458 16.1458 8.17276C16.1458 7.79026 15.9922 7.42343 15.7187 7.15296C15.4452 6.88249 15.0743 6.73054 14.6875 6.73054ZM10.3125 6.73054C10.0241 6.73054 9.74213 6.81513 9.5023 6.9736C9.26248 7.13207 9.07556 7.35732 8.96519 7.62085C8.85481 7.88438 8.82593 8.17436 8.8822 8.45412C8.93847 8.73389 9.07736 8.99086 9.28131 9.19256C9.48527 9.39426 9.74512 9.53162 10.028 9.58727C10.3109 9.64292 10.6041 9.61435 10.8706 9.5052C11.1371 9.39604 11.3648 9.21119 11.5251 8.97401C11.6853 8.73684 11.7708 8.458 11.7708 8.17276C11.7708 7.98337 11.7331 7.79583 11.6598 7.62085C11.5865 7.44587 11.4791 7.28688 11.3437 7.15296C11.2083 7.01904 11.0475 6.9128 10.8706 6.84033C10.6937 6.76785 10.504 6.73054 10.3125 6.73054Z"
                  fill="#004DBC"
                  fillRule="evenodd"
                />
              </svg>
            }
            width="40px"
            withPadding="small"
          />
        }
        isOpen={isOpen}
        tabItems={tabItems}
        tabPanes={tabPanes}
        title={t('CREATE_ACTIONS_MODAL.title')}
        onClose={onClose}
      />
    </>
  );
};

export default CreateActionsModal;
